import {
  sentryDsn,
  env,
  appVersion,
  apiBaseUrl,
  runtimeVersion,
} from "@constants/Env";
import { Platform } from "react-native";
import * as Device from "expo-device";
import axios from "axios";
import * as Sentry from "sentry-expo";
import { User, CaptureContext, SeverityLevel } from "@sentry/types";
import { ReactNativeWrapperOptions } from "@sentry/react-native/dist/js/options";
import ExceptionError from "@lib/errors/exceptionError";
import { SecureStoreManager } from "@lib/util/secureStoreManager";

Sentry.init({
  dsn: sentryDsn,
  tracesSampleRate: 1.0,
  enableInExpoDevelopment: true,
  enableNative: env !== "local",
});

export function wrap<P>(
  RootComponent: React.ComponentType<P>,
  options?: ReactNativeWrapperOptions
) {
  if (Platform.OS === "web") {
    return RootComponent;
  }
  return Sentry.Native.wrap(RootComponent as any, options);
}

export function setUser(user: User | null) {
  if (Platform.OS === "web") {
    return Sentry.Browser.setUser(user as any);
  }
  return Sentry.Native.setUser(user as any);
}

export function log(
  level: SeverityLevel,
  exception: any,
  captureContext?: CaptureContext
) {
  if (captureContext === undefined) {
    captureContext = { level };
  }
  if (level !== "error") {
    if (Platform.OS === "web") {
      return Sentry.Browser.captureMessage(exception, captureContext as any);
    }
    return Sentry.Native.captureMessage(exception, captureContext as any);
  }
  if (Platform.OS === "web") {
    return Sentry.Browser.captureException(exception, captureContext as any);
  }
  return Sentry.Native.captureException(exception, captureContext as any);
}

export async function sendError(
  error: ExceptionError,
  captureContext?: { extra: Record<string, unknown> }
) {
  log("error", error, captureContext);
}

export type NotificationLevel =
  | "error"
  | "warning"
  | "info"
  | "debug"
  | "monitor";

interface NotificationOptions {
  level?: NotificationLevel;
  skipDeviceInfo?: boolean;
}

export async function notify(data: any, options: NotificationOptions = {}) {
  const { level = "error", skipDeviceInfo = false } = options;

  if (!apiBaseUrl) {
    console.error("API base URL is not configured");
    return;
  }

  try {
    const deviceInfo = skipDeviceInfo
      ? undefined
      : {
          os: Device.osName,
          osVersion: Device.osVersion,
          modelName: Device.modelName,
          platform: Platform.OS,
          env,
          appVersion,
          runtimeVersion,
        };

    // Authorizationヘッダーを取得（RelayEnvironmentと同じ方法を使用）
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    try {
      const authToken = await SecureStoreManager.getAccessToken();
      if (authToken !== null) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }
    } catch (storageError) {
      // ストレージアクセスエラーは無視（認証なしでリクエスト）
      console.warn("Failed to get auth token:", storageError);
    }

    // dataが文字列の場合はオブジェクトにラップ
    const formattedData = typeof data === "string" ? { message: data } : data;

    await axios.post(
      `${apiBaseUrl}/api/v1/frontend_logs`,
      {
        frontend_log: {
          level,
          data: formattedData,
          device_info: deviceInfo,
        },
      },
      { headers }
    );
  } catch (e) {
    console.error(`Failed to send frontend log (${level}):`, e);
  }
}

// 後方互換性のため、エラー専用の関数もエクスポート
export async function notifyError(error: any) {
  return notify(error, { level: "error" });
}
